import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Plus, Trash, Key, DownloadSimple, Crown, User as UserIcon } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";

const emptyForm = { username: "", name: "", password: "", role: "employee" };

export default function Users() {
  const { user: me } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [pwOpen, setPwOpen] = useState(null); // user object being reset
  const [pwNew, setPwNew] = useState("");

  const load = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.username.trim() || !form.password) { toast.error("Username and password required"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false); setForm(emptyForm); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (u, active) => {
    try {
      await api.put(`/users/${u.id}`, { active });
      toast.success(`${active ? "Enabled" : "Disabled"} ${u.username}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/users/${u.id}`, { role });
      toast.success(`${u.username} → ${role}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user "${u.username}"? This can't be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const resetPw = async () => {
    if (!pwNew || pwNew.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post(`/users/${pwOpen.id}/reset-password`, { new_password: pwNew });
      toast.success(`Password reset for ${pwOpen.username}`);
      setPwOpen(null); setPwNew("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const exportXlsx = () => {
    // Anchor download hits the protected endpoint via cookie
    const a = document.createElement("a");
    a.href = `${API_BASE}/users/export/xlsx`;
    a.rel = "noreferrer";
    a.click();
  };

  return (
    <div>
      <PageHeader
        title="Team & Access"
        subtitle={`${list.length} user${list.length === 1 ? "" : "s"}`}
        actions={
          <>
            <button className="btn-ghost flex items-center gap-2" onClick={exportXlsx} data-testid="users-export-xlsx">
              <DownloadSimple size={16} /> Export Roster (XLSX)
            </button>
            <button className="btn-primary flex items-center gap-2" onClick={() => setOpen(true)} data-testid="users-add-btn">
              <Plus size={16} /> Add User
            </button>
          </>
        }
      />

      <div className="surface overflow-x-auto fade-up" data-testid="users-table">
        <table className="data-table">
          <thead>
            <tr><th>Username</th><th>Name</th><th>Role</th><th>Status</th><th>Last Login</th><th></th></tr>
          </thead>
          <tbody>
            {list.map((u) => {
              const isSelf = u.id === me?.id;
              return (
                <tr key={u.id} className="hover:bg-transparent cursor-default" data-testid={`user-row-${u.username}`}>
                  <td className="font-mono text-slate-900 flex items-center gap-2">
                    {u.role === "owner" ? <Crown size={14} className="text-amber-400" /> : <UserIcon size={14} className="text-slate-400" />}
                    {u.username}
                    {isSelf && <span className="text-xs text-emerald-300">(you)</span>}
                  </td>
                  <td>{u.name || "—"}</td>
                  <td>
                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={isSelf}>
                      <SelectTrigger className="h-8 w-32" data-testid={`user-role-${u.username}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="employee">Employee</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <Switch checked={u.active !== false} onCheckedChange={(v) => toggleActive(u, v)} disabled={isSelf} data-testid={`user-active-${u.username}`} />
                      <span className="text-xs text-slate-500">{u.active !== false ? "Active" : "Disabled"}</span>
                    </div>
                  </td>
                  <td className="font-mono text-xs text-slate-500">{u.last_login ? new Date(u.last_login).toLocaleString() : "—"}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <button className="text-slate-400 hover:text-slate-900 p-1" onClick={() => { setPwOpen(u); setPwNew(""); }} data-testid={`user-reset-${u.username}`} title="Reset password">
                        <Key size={16} />
                      </button>
                      <button className="text-slate-400 hover:text-red-400 p-1 disabled:opacity-30" onClick={() => remove(u)} disabled={isSelf} data-testid={`user-delete-${u.username}`} title="Delete user">
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="users-add-dialog">
          <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 py-2">
            <div>
              <Label className="text-xs text-slate-600">Username *</Label>
              <Input data-testid="users-new-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Full Name</Label>
              <Input data-testid="users-new-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Password *</Label>
              <Input data-testid="users-new-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <p className="text-xs text-slate-400 mt-1">Minimum 6 characters. Share this with the employee once — they can change it later.</p>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="users-new-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee (daily ops only)</SelectItem>
                  <SelectItem value="owner">Owner (full access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <button className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={create} data-testid="users-save-btn">Create User</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwOpen} onOpenChange={(o) => !o && setPwOpen(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reset Password{pwOpen && ` — ${pwOpen.username}`}</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label className="text-xs text-slate-600">New Password</Label>
            <Input type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} data-testid="users-reset-input" />
            <p className="text-xs text-slate-400 mt-2">Share this with the user; they'll use it next time they sign in.</p>
          </div>
          <DialogFooter>
            <button className="btn-ghost" onClick={() => setPwOpen(null)}>Cancel</button>
            <button className="btn-primary" onClick={resetPw} data-testid="users-reset-confirm">Reset</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
